
import React, { useState, useEffect } from 'react';
import { Professional } from '../types';
import ListingCard from '../components/ListingCard';
import { Heart, ArrowLeft, Trash2, Mail, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { listingService } from '../services/listingService';

const ShortlistPage: React.FC = () => {
  const [shortlist, setShortlist] = useState<Professional[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadShortlist = async () => {
      setLoading(true);
      try {
        const savedIds = JSON.parse(localStorage.getItem('planstack_shortlist') || '[]');
        if (savedIds.length === 0) {
          setShortlist([]);
          return;
        }
        
        const allPros = await listingService.getAllListings();
        const results = allPros.filter(p => savedIds.includes(p.id));
        setShortlist(results);
      } catch (error) {
        console.error("Failed to load shortlist:", error);
      } finally {
        setLoading(false);
      }
    };

    loadShortlist();
  }, []);

  const clearShortlist = () => {
    if (confirm('Are you sure you want to clear your entire shortlist?')) {
      localStorage.setItem('planstack_shortlist', '[]');
      setShortlist([]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="animate-spin text-brand-600" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
           <div>
              <Link to="/directory" className="inline-flex items-center gap-1.5 text-brand-600 font-bold text-sm mb-4 hover:underline">
                 <ArrowLeft size={16} /> Back to Directory
              </Link>
              <h1 className="text-4xl font-black text-gray-900 flex items-center gap-4">
                 <Heart className="text-red-500" fill="currentColor" size={36} /> My Shortlist
              </h1>
              <p className="text-gray-500 mt-2 font-medium">You have saved {shortlist.length} consultants for review.</p>
           </div>
           
           {shortlist.length > 0 && (
             <div className="flex gap-4">
                <button 
                   onClick={() => window.print()} 
                   className="bg-white text-gray-700 border border-gray-200 px-6 py-3 rounded-xl font-bold text-sm shadow-sm hover:bg-gray-50 transition"
                >
                   Print Summary
                </button>
                <button 
                   onClick={clearShortlist}
                   className="bg-white text-red-500 border border-red-100 px-6 py-3 rounded-xl font-bold text-sm shadow-sm hover:bg-red-50 transition flex items-center gap-2"
                >
                   <Trash2 size={16} /> Clear All
                </button>
             </div>
           )}
        </div>

        {shortlist.length > 0 ? (
          <div className="space-y-6">
             <div className="bg-brand-900 text-white p-8 rounded-3xl shadow-xl flex flex-col md:flex-row justify-between items-center gap-6 mb-10">
                <div>
                   <h3 className="text-xl font-bold">Bulk Enquiry</h3>
                   <p className="text-brand-200 text-sm mt-1">Send your project details to all {shortlist.length} consultants at once.</p>
                </div>
                <button className="bg-accent-500 hover:bg-accent-600 text-brand-900 font-black px-10 py-4 rounded-2xl transition shadow-lg flex items-center gap-2 uppercase tracking-widest text-xs">
                   <Mail size={18} /> Send Bulk Enquiries
                </button>
             </div>

             <div className="grid grid-cols-1 gap-6">
                {shortlist.map(pro => (
                  <ListingCard key={pro.id} professional={pro} />
                ))}
             </div>
          </div>
        ) : (
          <div className="bg-white p-20 rounded-3xl border border-dashed border-gray-300 text-center">
             <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-8">
                <Heart size={48} className="text-gray-200" />
             </div>
             <h2 className="text-2xl font-black text-gray-900 mb-4">Your shortlist is empty</h2>
             <p className="text-gray-500 max-w-sm mx-auto mb-10">Browse the directory and click the heart icon to save consultants you'd like to work with.</p>
             <Link to="/directory" className="bg-brand-900 text-white font-black px-12 py-4 rounded-2xl shadow-xl hover:bg-black transition uppercase tracking-widest text-sm">
                Explore Directory
             </Link>
          </div>
        )}

      </div>
    </div>
  );
};

export default ShortlistPage;
